const cTIMEZONE_LUXON = 'America/Sao_Paulo';

// Libs
import { DateTime } from 'luxon';
import EMacroFiltroExecucao from '../enums/EMacroSubstituicao';

export type TMesesAno = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

type TFormatosPossiveisFormatacaoData =
  | 'yyyy-MM-ddTHH:mm:ss'
  | 'yyyy-MM-dd HH:mm:ss'
  | 'dd.MM.yyyy HH:mm:ss'
  | 'dd/MM/yyyy HH:mm:ss'
  | 'dd/MM/yyyy, HH:mm:ss'
  | 'MM-dd-yyyy HH:mm:ss'
  | 'yyyy/MM/dd HH:mm:ss'
  | 'dd/MM/yyyy HH:mm'
  | 'yyyy-MM-dd'
  | 'dd.MM.yyyy'
  | 'dd/MM/yyyy';

export default class DateUtils {
  static formatar(
    pData: Date | string,
    pFormatoSaida: TFormatosPossiveisFormatacaoData = 'dd/MM/yyyy HH:mm:ss',
  ): string {
    if (!pData) return '';
    const luxonDate = DateUtils.parse(pData);
    return luxonDate.toFormat(pFormatoSaida);
  }

  static parse(pData: string | Date) {
    try {
      if (pData instanceof Date) {
        return DateTime.fromJSDate(pData).setZone(cTIMEZONE_LUXON);
      }

      // Tenta parse ISO direto (resolve casos com timezone ex: 2025-04-12T20:49:20.417-03:00)
      const iso = DateTime.fromISO(pData, { setZone: true });
      if (iso.isValid) {
        return iso.setZone(cTIMEZONE_LUXON); // força exibição/foco no fuso esperado
      }

      const formatosPossiveis: TFormatosPossiveisFormatacaoData[] = [
        'yyyy-MM-ddTHH:mm:ss',
        'yyyy-MM-dd HH:mm:ss',
        'dd.MM.yyyy HH:mm:ss',
        'dd/MM/yyyy HH:mm:ss',
        'dd/MM/yyyy, HH:mm:ss',
        'MM-dd-yyyy HH:mm:ss',
        'yyyy/MM/dd HH:mm:ss',
        'dd/MM/yyyy HH:mm',
        'yyyy-MM-dd',
        'dd.MM.yyyy',
        'dd/MM/yyyy',
      ];

      for (const formato of formatosPossiveis) {
        const dt = DateTime.fromFormat(pData, formato, {
          setZone: true,
          zone: cTIMEZONE_LUXON,
          locale: 'pt-BR',
        });

        if (dt.isValid) {
          return dt.setZone(cTIMEZONE_LUXON);
        }
      }

      throw `Data inválida ou formato não reconhecido: "${pData}"`;
    } catch (error) {
      throw error;
    }
  }

  static toISO8601(strData: string): string {
    if (!DateUtils.isDateTimeStr(strData)) return '';
    const [data, hora] = strData.split(' ');
    const [dia, mes, ano] = data.split('/');
    const [horaStr, minutoStr] = hora.split(':');

    // Garante que mês e dia tenham dois dígitos
    const pad = (s: string) => s.padStart(2, '0');

    return `${ano}-${pad(mes)}-${pad(dia)}T${pad(horaStr)}:${pad(minutoStr)}`;
  }

  static agora(): DateTime {
    return DateTime.now().setZone(cTIMEZONE_LUXON);
  }

  static agoraJSDate(): Date {
    return DateTime.utc().toJSDate();
  }

  static fromISO8601(dataISO: string): string {
    if (!DateUtils.isISO8601Date(dataISO)) return '';

    const [data, hora] = dataISO.split('T');
    if (!data || !hora) return '';

    const [ano, mes, dia] = data.split('-');
    const [horaStr, minutoStr] = hora.split(':');

    return `${dia}/${mes}/${ano} ${horaStr}:${minutoStr}`;
  }

  static isISO8601Date(value: string): boolean {
    const regexDataHora_ISO8601 = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d$/;
    return regexDataHora_ISO8601.test(value);
  }

  static isDateTimeStr(value: string): boolean {
    const regexDataHora = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4} ([01][0-9]|2[0-3]):[0-5][0-9]$/;
    return regexDataHora.test(value);
  }

  static dataHoraInicioMesAtual(): string {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayDate = `${firstDay.toLocaleDateString('pt-BR')} 00:00`;
    return firstDayDate;
  }

  static dataHoraFimMesAtual(): string {
    const date = new Date();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const lastDayDate = `${lastDay.toLocaleDateString('pt-BR')} 23:59`;
    return lastDayDate;
  }

  static strToDate(dataStr: string): Date | null {
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4} ([01][0-9]|2[0-3]):[0-5][0-9]$/;

    if (!regex.test(dataStr)) {
      return null;
    }

    const [dia, mes, anoHora] = dataStr.split('/');
    const [ano, horaMinuto] = anoHora.split(' ');
    const [hora, minuto] = horaMinuto.split(':');

    return new Date(Number(ano), Number(mes) - 1, Number(dia), Number(hora), Number(minuto));
  }

  static aplicarMacroSubstituicao(pMacro: EMacroFiltroExecucao): DateTime {
    try {
      const dt = DateTime.fromJSDate(new Date()).setZone('America/Sao_Paulo');
      let result: DateTime;

      switch (pMacro) {
        case EMacroFiltroExecucao.Anteontem:
          result = dt.minus({ days: 2 });
          break;
        case EMacroFiltroExecucao.Ontem:
          result = dt.minus({ days: 1 });
          break;
        case EMacroFiltroExecucao.Hoje:
          result = dt;
          break;
        case EMacroFiltroExecucao.Amanha:
          result = dt.plus({ days: 1 });
          break;
        case EMacroFiltroExecucao.InicioMesAtual:
          result = dt.startOf('month');
          break;
        case EMacroFiltroExecucao.FimMesAtual:
          result = dt.endOf('month');
          break;
        case EMacroFiltroExecucao.InicioDezAnosAtras:
          result = dt.minus({ years: 10 }).startOf('year');
          break;
        case EMacroFiltroExecucao.InicioCincoAnosAtras:
          result = dt.minus({ years: 5 }).startOf('year');
          break;
        case EMacroFiltroExecucao.AnoPosterior:
          result = dt.plus({ years: 1 }).startOf('year');
          break;
        case EMacroFiltroExecucao.InicioAnoAnterior:
          result = dt.minus({ years: 1 }).startOf('year');
          break;
        case EMacroFiltroExecucao.FimAnoAnterior:
          result = dt.minus({ years: 1 }).endOf('year');
          break;
        case EMacroFiltroExecucao.InicioAnoAtual:
          result = dt.startOf('year');
          break;
        case EMacroFiltroExecucao.FimAnoAtual:
          result = dt.endOf('year');
          break;
        case EMacroFiltroExecucao.InicioAnoPosterior:
          result = dt.plus({ years: 1 }).startOf('year');
          break;
        case EMacroFiltroExecucao.FimAnoPosterior:
          result = dt.plus({ years: 1 }).endOf('year');
          break;
        default:
          throw `O macro: "${pMacro}" não foi mapeado e não é esperado dentro da função de macro substituição.`;
      }

      return result;
    } catch (error) {
      throw error;
    }
  }

  static mesPorExtenso(pMes: number): string {
    switch (pMes) {
      case 1:
        return 'JANEIRO';
      case 2:
        return 'FEVEREIRO';
      case 3:
        return 'MARÇO';
      case 4:
        return 'ABRIL';
      case 5:
        return 'MAIO';
      case 6:
        return 'JUNHO';
      case 7:
        return 'JULHO';
      case 8:
        return 'AGOSTO';
      case 9:
        return 'SETEMBRO';
      case 10:
        return 'OUTUBRO';
      case 11:
        return 'NOVEMBRO';
      case 12:
        return 'DEZEMBRO';
      default:
        throw `O mês informando: "${pMes}" está abaixo de 1 ou acima de 12.`;
    }
  }

  static mesPorExtensoAbreviado(pMes: number): string {
    return DateUtils.mesPorExtenso(pMes).slice(0, 3);
  }

  /**
   * Método para retornar a hora "mentirosa" do ERP
   * @param pDateTime
   * @returns date time string sem fuso
   */
  static dataERPFromDateTimeString(pDateTime: string) {
    return pDateTime.replace('-03:00', 'Z');
  }

  /**
   * Retorna um DateTime com a zona correta.
   * ERP envia um hora "mentirosa". Ex: 2026-02-03T09:31:20.734Z, indicando que este horário é sem fuso.
   * Porém, deveria vir como 2026-02-03T09:31:20.734-03:00 pois utiliza o horário de Brasília.
   * @param dataERP data obtida pelo ERP
   * @returns DateTime com a zona correta
   */
  static dateTimeFromDataERP(dataERP: string) {
    // Remover o Z para setar nova zona
    dataERP = dataERP.replace('Z', '');
    return DateTime.fromISO(dataERP, { zone: 'America/Sao_Paulo', setZone: true });
  }

  static dateTimeAsStringFromDataERP(dataERP: string): string {
    const data = DateUtils.dateTimeFromDataERP(dataERP);
    const dataString = data.toISO();

    if (!dataString) {
      throw new Error(`Não foi possível converter a data ERP: ${dataERP}`);
    }

    return dataString;
  }

  static obterAnosEntreDatas(pDataInicial: DateTime, pDataFinal: DateTime): number[] {
    // Identifica qual data é a menor e qual é a maior
    const dataInicio = pDataInicial < pDataFinal ? pDataInicial : pDataFinal;
    const dataFim = pDataInicial < pDataFinal ? pDataFinal : pDataInicial;

    // Extrai apenas os números dos anos para evitar manipulação desnecessária de datas
    let anoAtual = dataInicio.year;
    const anoFinal = dataFim.year;

    const listaAnos = [];

    // 3. Preenche o array com os anos do intervalo
    while (anoAtual <= anoFinal) {
      listaAnos.push(anoAtual);
      anoAtual++;
    }

    return listaAnos;
  }

  static obterDiasEntreDatas(pDataInicial: DateTime, pDataFinal: DateTime): number {
    // Identifica qual data é a menor e qual é a maior
    const dataInicio = pDataInicial < pDataFinal ? pDataInicial : pDataFinal;
    const dataFim = pDataInicial < pDataFinal ? pDataFinal : pDataInicial;

    // Extrai apenas a quantidade de dias para evitar manipulação desnecessária de datas
    return dataFim.diff(dataInicio, 'days').days;
  }
}
